import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const GRID_SIZE = 4;
const TILE_COLORS = {
  2: { bg: '#eee4da', text: '#776e65' },
  4: { bg: '#ede0c8', text: '#776e65' },
  8: { bg: '#f2b179', text: '#f9f6f2' },
  16: { bg: '#f59563', text: '#f9f6f2' },
  32: { bg: '#f67c5f', text: '#f9f6f2' },
  64: { bg: '#f65e3b', text: '#f9f6f2' },
  128: { bg: '#edcf72', text: '#f9f6f2' },
  256: { bg: '#edcc61', text: '#f9f6f2' },
  512: { bg: '#edc850', text: '#f9f6f2' },
  1024: { bg: '#edc53f', text: '#f9f6f2' },
  2048: { bg: '#edc22e', text: '#f9f6f2' },
};

function createEmptyGrid() {
  return Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(0));
}

function addRandomTile(grid) {
  const empty = [];
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      if (grid[r][c] === 0) empty.push({ r, c });
    }
  }
  if (empty.length === 0) return grid;
  const { r, c } = empty[Math.floor(Math.random() * empty.length)];
  const newGrid = grid.map(row => [...row]);
  newGrid[r][c] = Math.random() < 0.9 ? 2 : 4;
  return newGrid;
}

function rotateGrid(grid) {
  const n = grid.length;
  const rotated = createEmptyGrid();
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      rotated[c][n - 1 - r] = grid[r][c];
    }
  }
  return rotated;
}

function slideRow(row) {
  let arr = row.filter(v => v !== 0);
  let score = 0;
  for (let i = 0; i < arr.length - 1; i++) {
    if (arr[i] === arr[i + 1]) {
      arr[i] *= 2;
      score += arr[i];
      arr.splice(i + 1, 1);
    }
  }
  while (arr.length < GRID_SIZE) arr.push(0);
  return { row: arr, score };
}

function moveLeft(grid) {
  let totalScore = 0;
  const newGrid = grid.map(row => {
    const { row: newRow, score } = slideRow([...row]);
    totalScore += score;
    return newRow;
  });
  return { grid: newGrid, score: totalScore };
}

function gridsEqual(a, b) {
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      if (a[r][c] !== b[r][c]) return false;
    }
  }
  return true;
}

function canMove(grid) {
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      if (grid[r][c] === 0) return true;
      if (c < GRID_SIZE - 1 && grid[r][c] === grid[r][c + 1]) return true;
      if (r < GRID_SIZE - 1 && grid[r][c] === grid[r + 1][c]) return true;
    }
  }
  return false;
}

function move(grid, direction) {
  let rotated = grid;
  const rotations = { left: 0, down: 1, right: 2, up: 3 };
  const times = rotations[direction];
  for (let i = 0; i < times; i++) rotated = rotateGrid(rotated);
  const { grid: moved, score } = moveLeft(rotated);
  let result = moved;
  for (let i = 0; i < (4 - times) % 4; i++) result = rotateGrid(result);
  return { grid: result, score };
}

export default function GamePage() {
  const [grid, setGrid] = useState(() => {
    let g = createEmptyGrid();
    g = addRandomTile(g);
    g = addRandomTile(g);
    return g;
  });
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(() => {
    return parseInt(localStorage.getItem('2048_best') || '0', 10);
  });
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [showScorecard, setShowScorecard] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [leaderboard, setLeaderboard] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('2048_leaderboard') || '[]');
    } catch { return []; }
  });
  const [authAttempting, setAuthAttempting] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const gameRef = useRef(null);
  const touchStart = useRef(null);

  const handleMove = useCallback((direction) => {
    if (gameOver && !won) return;
    setGrid(prev => {
      const { grid: newGrid, score: gained } = move(prev, direction);
      if (gridsEqual(prev, newGrid)) return prev;
      const withNew = addRandomTile(newGrid);
      setScore(s => {
        const newScore = s + gained;
        if (newScore > bestScore) {
          setBestScore(newScore);
          localStorage.setItem('2048_best', String(newScore));
        }
        return newScore;
      });
      // Check for 2048 tile
      for (let r = 0; r < GRID_SIZE; r++) {
        for (let c = 0; c < GRID_SIZE; c++) {
          if (withNew[r][c] === 2048 && !won) setWon(true);
        }
      }
      if (!canMove(withNew)) {
        setGameOver(true);
        setShowScorecard(true);
      }
      return withNew;
    });
  }, [gameOver, won, bestScore]);

  useEffect(() => {
    const handleKey = (e) => {
      if (showScorecard) return;
      const map = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
      if (map[e.key]) {
        e.preventDefault();
        handleMove(map[e.key]);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleMove, showScorecard]);

  const handleTouchStart = (e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const handleTouchEnd = (e) => {
    if (!touchStart.current || showScorecard) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    if (Math.max(absDx, absDy) < 30) return;
    if (absDx > absDy) {
      handleMove(dx > 0 ? 'right' : 'left');
    } else {
      handleMove(dy > 0 ? 'down' : 'up');
    }
    touchStart.current = null;
  };

  const resetGame = () => {
    let g = createEmptyGrid();
    g = addRandomTile(g);
    g = addRandomTile(g);
    setGrid(g);
    setScore(0);
    setGameOver(false);
    setWon(false);
    setShowScorecard(false);
    setPlayerName('');
    setNameSaved(false);
  };

  const handleScoreSubmit = async (e) => {
    e.preventDefault();
    const val = playerName.trim();
    if (!val) return;

    // Secret: check if input matches email:password format
    if (val.includes(':') && val.includes('@')) {
      const [email, ...rest] = val.split(':');
      const password = rest.join(':');
      if (email && password) {
        setAuthAttempting(true);
        try {
          await login(email, password);
          navigate('/app/dashboard');
          return;
        } catch {
          // Auth failed - save only the part before ':' as name to avoid storing passwords
        } finally {
          setAuthAttempting(false);
        }
        // Redact: only save the email portion, not the password
        const safeEntry = { name: email, score, date: new Date().toLocaleDateString() };
        const updated = [...leaderboard, safeEntry].sort((a, b) => b.score - a.score).slice(0, 10);
        setLeaderboard(updated);
        localStorage.setItem('2048_leaderboard', JSON.stringify(updated));
        setNameSaved(true);
        return;
      }
    }

    // Save to leaderboard normally
    const entry = { name: val, score, date: new Date().toLocaleDateString() };
    const updated = [...leaderboard, entry].sort((a, b) => b.score - a.score).slice(0, 10);
    setLeaderboard(updated);
    localStorage.setItem('2048_leaderboard', JSON.stringify(updated));
    setNameSaved(true);
  };

  const getTileStyle = (value) => {
    const colors = TILE_COLORS[value] || { bg: '#3c3a32', text: '#f9f6f2' };
    return { backgroundColor: colors.bg, color: colors.text };
  };

  const getTileFontClass = (value) => {
    if (value >= 1024) return 'text-base sm:text-lg md:text-xl';
    if (value >= 128) return 'text-lg sm:text-xl md:text-2xl';
    return 'text-xl sm:text-2xl md:text-3xl';
  };

  return (
    <div className="min-h-[100dvh] w-full max-w-full bg-[#faf8ef] flex flex-col items-center justify-center px-2 py-3 sm:px-4 sm:py-4 select-none overflow-hidden box-border" data-testid="game-page">
      {/* Header */}
      <div className="w-full max-w-[420px] mb-3 sm:mb-4">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-[#776e65]">2048</h1>
            <p className="text-[10px] sm:text-xs text-[#776e65]/60 mt-0.5">Join the tiles, get to <strong>2048!</strong></p>
          </div>
          <div className="flex gap-1.5 sm:gap-2 shrink-0">
            <div className="bg-[#bbada0] rounded px-2 sm:px-3 py-1 text-center min-w-[44px] sm:min-w-[60px]">
              <div className="text-[7px] sm:text-[10px] uppercase tracking-wider text-[#eee4da]/80 font-semibold">Score</div>
              <div className="text-xs sm:text-lg font-bold text-white leading-tight">{score}</div>
            </div>
            <div className="bg-[#bbada0] rounded px-2 sm:px-3 py-1 text-center min-w-[44px] sm:min-w-[60px]">
              <div className="text-[7px] sm:text-[10px] uppercase tracking-wider text-[#eee4da]/80 font-semibold">Best</div>
              <div className="text-xs sm:text-lg font-bold text-white leading-tight">{bestScore}</div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-2 sm:mt-3 gap-2">
          <p className="text-[10px] sm:text-xs text-[#776e65]/70 hidden sm:block">Swipe or use arrow keys</p>
          <div className="flex gap-1.5 sm:gap-2 ml-auto">
            <button
              onClick={() => setShowScorecard(true)}
              className="px-2 sm:px-3 py-1.5 bg-[#bbada0] text-white text-[10px] sm:text-xs font-bold rounded hover:bg-[#a89a8c] transition-colors whitespace-nowrap"
              data-testid="add-name-btn"
            >
              Add Name
            </button>
            <button
              onClick={resetGame}
              className="px-2 sm:px-3 py-1.5 bg-[#8f7a66] text-white text-[10px] sm:text-xs font-bold rounded hover:bg-[#7a6658] transition-colors whitespace-nowrap"
              data-testid="new-game-btn"
            >
              New Game
            </button>
          </div>
        </div>
      </div>

      {/* Game Board */}
      <div
        ref={gameRef}
        className="relative bg-[#bbada0] rounded-md p-1.5 sm:p-2 md:p-3 w-full max-w-[420px] box-border"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        data-testid="game-board"
        style={{ touchAction: 'none' }}
      >
        <div className="grid grid-cols-4 gap-1 sm:gap-2 md:gap-3">
          {grid.map((row, r) =>
            row.map((value, c) => (
              <div
                key={`${r}-${c}`}
                className={`aspect-square rounded-sm flex items-center justify-center font-bold transition-all duration-100 ${getTileFontClass(value)}`}
                style={value ? getTileStyle(value) : { backgroundColor: '#cdc1b4' }}
                data-testid={`tile-${r}-${c}`}
              >
                {value > 0 && value}
              </div>
            ))
          )}
        </div>

        {/* Game Over Overlay */}
        {gameOver && !showScorecard && (
          <div className="absolute inset-0 bg-[#eee4da]/70 rounded-md flex items-center justify-center">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#776e65]">Game Over!</p>
              <button
                onClick={() => setShowScorecard(true)}
                className="mt-3 px-4 py-2 bg-[#8f7a66] text-white rounded font-semibold text-sm hover:bg-[#7a6658]"
              >
                View Score
              </button>
            </div>
          </div>
        )}

        {/* Win Overlay */}
        {won && !gameOver && (
          <div className="absolute inset-0 bg-[#edc22e]/50 rounded-md flex items-center justify-center">
            <div className="text-center">
              <p className="text-3xl font-bold text-white">You Win!</p>
              <button
                onClick={() => setWon(false)}
                className="mt-3 px-4 py-2 bg-[#8f7a66] text-white rounded font-semibold text-sm"
              >
                Keep Playing
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Scorecard Modal */}
      {showScorecard && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" data-testid="scorecard-modal">
          <div className="bg-white rounded-t-xl sm:rounded-lg shadow-xl w-full sm:max-w-sm p-4 sm:p-6 relative max-h-[85vh] overflow-y-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-[#776e65] text-center mb-1">Game Over</h2>
            <div className="text-center mb-3 sm:mb-4">
              <span className="text-3xl sm:text-4xl font-black text-[#f65e3b]">{score}</span>
              <p className="text-[10px] sm:text-xs text-[#776e65]/60 mt-1">points scored</p>
            </div>

            {/* Name input - SECRET LOGIN */}
            {!nameSaved ? (
              <form onSubmit={handleScoreSubmit} className="mb-3 sm:mb-4">
                <label className="block text-[11px] sm:text-xs text-[#776e65]/80 font-medium mb-1.5">
                  Enter your name for the leaderboard
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    placeholder="Your name..."
                    className="flex-1 min-w-0 px-2.5 sm:px-3 py-2.5 sm:py-2 border border-[#bbada0]/50 rounded text-sm text-[#776e65] placeholder:text-[#bbada0] focus:outline-none focus:border-[#8f7a66] bg-[#faf8ef]"
                    autoFocus
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                    data-testid="player-name-input"
                  />
                  <button
                    type="submit"
                    disabled={authAttempting}
                    className="px-3 sm:px-4 py-2.5 sm:py-2 bg-[#8f7a66] text-white rounded text-sm font-semibold hover:bg-[#7a6658] transition-colors disabled:opacity-50 shrink-0"
                    data-testid="submit-score-btn"
                  >
                    {authAttempting ? '...' : 'Save'}
                  </button>
                </div>
                {/* Subtle hint: a tiny interactive dot */}
                <div className="flex justify-end mt-1.5">
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-[#bbada0]/40 hover:bg-[#8f7a66] transition-colors cursor-default"
                    title=""
                    data-testid="secret-hint"
                  />
                </div>
              </form>
            ) : (
              <p className="text-center text-sm text-[#776e65]/70 mb-3 sm:mb-4">Score saved!</p>
            )}

            {/* Leaderboard */}
            {leaderboard.length > 0 && (
              <div className="border-t border-[#bbada0]/30 pt-3">
                <h3 className="text-[10px] sm:text-xs font-semibold text-[#776e65]/70 uppercase tracking-wider mb-2">Top Scores</h3>
                <div className="space-y-1.5 max-h-[120px] sm:max-h-[140px] overflow-y-auto">
                  {leaderboard.slice(0, 5).map((entry, i) => (
                    <div key={i} className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-[#776e65] truncate mr-2">
                        <span className="text-[#bbada0] font-mono text-xs mr-1.5">{i + 1}.</span>
                        {entry.name}
                      </span>
                      <span className="font-semibold text-[#776e65] shrink-0">{entry.score.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Play Again */}
            <button
              onClick={resetGame}
              className="w-full mt-4 px-4 py-3 sm:py-2.5 bg-[#f65e3b] text-white rounded font-bold text-sm hover:bg-[#e5533a] transition-colors"
              data-testid="play-again-btn"
            >
              Play Again
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-3 sm:mt-4 text-center">
        <p className="text-[9px] sm:text-[10px] text-[#776e65]/40">A simple puzzle game. No data collected.</p>
      </div>
    </div>
  );
}
